// src/components/ui/EmailVerificationModal.jsx - FIXED BUTTONS

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { resendVerification } from '@/services/apiService';

function classNames(...xs) {
  return xs.filter(Boolean).join(' ');
}

export const EmailVerificationModal = ({ email, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // ✅ Handle cooldown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handleResend = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await resendVerification(email);
      setSuccess('✓ Verification email sent! Check your inbox.');
      setCooldownSeconds(60); // Start 60-second cooldown
    } catch (err) {
      const status = err?.response?.status;
      
      // ✅ Handle 429 (Too Many Requests) rate limit
      if (status === 429) {
        const retryAfter = err?.response?.data?.retry_after || 60;
        setCooldownSeconds(retryAfter);
        setError(`Please wait ${retryAfter} seconds before resending.`);
      } else {
        setError(
          err?.response?.data?.error ||
          'Failed to resend verification email. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="bg-stone-900 rounded-lg shadow-lg p-8 max-w-md w-full mx-4 border border-zinc-800"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-2xl font-bold mb-4 text-swan">Verify Your Email</h2>

        <p className="text-field-grey mb-6">
          A verification link has been sent to <strong className="text-mikado-yellow">{email}</strong>. Please check your inbox and click the link to verify your email address.
        </p>

        <p className="text-sm text-field-grey mb-6">
          You can use all features except submitting to competitions until your email is verified.
        </p>

        {error && (
          <motion.p
            className="text-imperial-red text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error}
          </motion.p>
        )}
        
        {success && (
          <motion.p
            className="text-mikado-yellow text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {success}
          </motion.p>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={handleResend}
            disabled={loading || cooldownSeconds > 0}
            className={classNames(
              'inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium',
              loading || cooldownSeconds > 0
                ? 'border-zinc-700 text-field-grey opacity-50 cursor-not-allowed'
                : 'border-zinc-800 text-swan hover:bg-zinc-900'
            )}
          >
            {cooldownSeconds > 0
              ? `Resend in ${cooldownSeconds}s`
              : loading
              ? 'Sending...'
              : 'Resend Verification Email'}
          </button>

          <Button
            onClick={onClose}
            variant="primary"
            className="w-full"
          >
            Continue to App
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmailVerificationModal;